import { useMemo, useEffect, useRef } from 'react';
import {
	LuPin,
	LuPencil,
	LuTrash2,
	LuEye,
	LuEyeOff,
} from 'react-icons/lu';
import { Popconfirm, Tag } from 'antd';
import { ml } from 'src/_shared/services/utils.js';
import {
	stripHtml,
	formatDate,
	lightenHex,
	accentRgba,
	darkenHex,
} from './utils.js';
import { getAntTagColor } from './constants.js';

function AnnouncementCard({
	announcement,
	isModerator,
	onReadMore,
	onEdit,
	onDelete,
	onTogglePin,
	languageCode,
	currentUser,
	allMultiLingualData,
	accentColor,
	gradientCssLooping,
	pinnedColor,
	showAuthor,
	showViews,
	defaultAuthor,
}) {
	const thumbnailUrl = useMemo(() => {
		if (announcement.thumbnailSignedUrl) return announcement.thumbnailSignedUrl;
		if (!announcement.content) return null;
		const match = announcement.content.match(/<img[^>]+src=["']([^"']+)["']/i);
		if (!match?.[1]) return null;
		// Decode HTML entities (e.g. &amp; → &) since the URL is extracted from HTML content
		return match[1].replace(/&amp;/g, '&');
	}, [announcement.thumbnailSignedUrl, announcement.content]);
	const plainContent = useMemo(
		() => stripHtml(announcement.content || ''),
		[announcement.content]
	);
	const tags = announcement.tags || [];
	const isPinned = announcement.pinned === true;
	const isHidden = announcement.hide === true;
	const controlsRef = useRef(null);

	useEffect(() => {
		const el = controlsRef.current;
		if (!el || !isModerator) return;

		const parent = el.closest('.group');
		if (!parent) return;

		const show = () => {
			el.style.opacity = '1';
			el.style.pointerEvents = 'auto';
		};

		const hide = () => {
			el.style.opacity = '0';
			el.style.pointerEvents = 'none';
		};

		parent.addEventListener('mouseenter', show);
		parent.addEventListener('mouseleave', hide);
		return () => {
			parent.removeEventListener('mouseenter', show);
			parent.removeEventListener('mouseleave', hide);
		};
	}, [isModerator]);

	return (
		<div
			className="group relative flex flex-col overflow-hidden rounded-2xl bg-white"
			style={{
				border: '1px solid #f1f5f9',
				minHeight: 400,
				height: '100%',
				boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
				transition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out, border-color 0.3s ease-out',
				willChange: 'transform',
				backfaceVisibility: 'hidden',
				WebkitBackfaceVisibility: 'hidden',
				perspective: 1000,
				cursor: 'pointer',
			}}
			onClick={() => onReadMore(announcement, thumbnailUrl)}
			onMouseOver={(e) => {
				e.currentTarget.style.boxShadow =
					'0 20px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05)';
				e.currentTarget.style.borderColor = '#e2e8f0';
			}}
			onMouseOut={(e) => {
				e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
				e.currentTarget.style.borderColor = '#f1f5f9';
			}}
		>
			{/* Pinned badge */}
			{isPinned && (
				<div
					className="absolute top-3 right-3 z-10"
					style={{
						width: 28,
						height: 28,
						borderRadius: '50%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						background: pinnedColor,
						color: '#fff',
						boxShadow: `0 4px 6px ${accentRgba(pinnedColor, 0.3)}`,
						lineHeight: 0,
					}}
				>
					<LuPin size={14} />
				</div>
			)}

			{/* Hidden badge */}
			{isHidden && isModerator && (
				<div
					className="absolute top-3 z-10 flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
					style={{
						left: isPinned ? 12 : undefined,
						right: isPinned ? undefined : 12,
						background: 'rgba(0,0,0,0.55)',
						color: '#fff',
						backdropFilter: 'blur(4px)',
					}}
				>
					<LuEyeOff size={10} />
					Hidden
				</div>
			)}

			{/* Moderator hover controls */}
			{isModerator && (
				<div
					ref={controlsRef}
					className="absolute top-3 left-3 z-20 flex gap-1.5"
					style={{
						opacity: 0,
						pointerEvents: 'none',
						transition: 'all 0.2s',
					}}
				>
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onTogglePin(announcement);
						}}
						style={{
							width: 28,
							height: 28,
							borderRadius: 8,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							backdropFilter: 'blur(8px)',
							boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
							border: 'none',
							cursor: 'pointer',
							background: isPinned
								? accentRgba(pinnedColor, 0.9)
								: 'rgba(255,255,255,0.9)',
							color: isPinned ? '#fff' : '#94a3b8',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							if (isPinned) {
								e.currentTarget.style.background = pinnedColor;
							} else {
								e.currentTarget.style.background = accentRgba(pinnedColor, 0.9);
								e.currentTarget.style.color = '#fff';
							}
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.background = isPinned
								? accentRgba(pinnedColor, 0.9)
								: 'rgba(255,255,255,0.9)';
							e.currentTarget.style.color = isPinned ? '#fff' : '#94a3b8';
						}}
						title={isPinned ? 'Unpin' : 'Pin'}
					>
						<LuPin size={13} />
					</button>
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onEdit(announcement);
						}}
						style={{
							width: 28,
							height: 28,
							borderRadius: 8,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							backdropFilter: 'blur(8px)',
							boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
							border: 'none',
							cursor: 'pointer',
							background: 'rgba(255,255,255,0.9)',
							color: '#475569',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.background = lightenHex(accentColor);
							e.currentTarget.style.color = '#fff';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
							e.currentTarget.style.color = '#475569';
						}}
						title="Edit"
					>
						<LuPencil size={13} />
					</button>
					<Popconfirm
						title="Delete this announcement?"
						description="This action cannot be undone."
						okText="Delete"
						okType="danger"
						cancelText="Cancel"
						onConfirm={(e) => {
							e?.stopPropagation();
							onDelete(announcement);
						}}
						onCancel={(e) => e?.stopPropagation()}
					>
						<button
							type="button"
							onClick={(e) => e.stopPropagation()}
							style={{
								width: 28,
								height: 28,
								borderRadius: 8,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: 0,
								lineHeight: 0,
								backdropFilter: 'blur(8px)',
								boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
								border: 'none',
								cursor: 'pointer',
								background: 'rgba(255,255,255,0.9)',
								color: '#475569',
								transition: 'all 0.2s',
							}}
							onMouseOver={(e) => {
								e.currentTarget.style.background = '#ef4444';
								e.currentTarget.style.color = '#fff';
							}}
							onMouseOut={(e) => {
								e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
								e.currentTarget.style.color = '#475569';
							}}
							title="Delete"
						>
							<LuTrash2 size={13} />
						</button>
					</Popconfirm>
				</div>
			)}

			{/* Thumbnail or gradient bar */}
			{thumbnailUrl ? (
				<div
					className="relative shrink-0 overflow-hidden"
					style={{ height: 176 }}
				>
					<img
						src={thumbnailUrl}
						alt={announcement.title}
						className="card-thumbnail-img h-full w-full object-cover"
					/>
					<div
						className="absolute inset-0"
						style={{
							background:
								'linear-gradient(to top, rgba(0,0,0,0.1), transparent)',
						}}
					/>
				</div>
			) : (
				<div
					className="rounded-t-2xl card-gradient-bar"
					style={{
						height: 12,
						backgroundImage: gradientCssLooping,
						backgroundSize: '200% 100%',
					}}
				/>
			)}

			{/* Content */}
			<div className="flex min-h-0 flex-1 flex-col" style={{ padding: 20 }}>
				{/* Tags */}
				{tags.length > 0 && (
					<div className="mb-3 flex flex-wrap gap-1.5">
						{tags.map((tag) => (
							<Tag
								key={tag}
								color={getAntTagColor(tag)}
								style={{ margin: 0 }}
							>
								{tag}
							</Tag>
						))}
					</div>
				)}

				{/* Title */}
				<h3
					className="mb-2 leading-snug"
					style={{
						fontSize: 15,
						fontWeight: 600,
						color: '#1e293b',
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						margin: 0,
						marginBottom: 8,
					}}
				>
					{announcement.title}
				</h3>

				{/* Date, author & views */}
				<div
					className="mb-3 flex items-center gap-2"
					style={{ fontSize: 11, lineHeight: 1, color: '#475569' }}
				>
					<span>{formatDate(announcement.dateCreated, languageCode)}</span>
					{showAuthor && (announcement.author || defaultAuthor) && (
						<>
							<span style={{ color: '#475569', fontWeight: 700 }}>
								&middot;
							</span>
							<span>{announcement.author || defaultAuthor}</span>
						</>
					)}
					{showViews && announcement.viewCount > 0 && (
						<>
							<span style={{ color: '#475569', fontWeight: 700 }}>
								&middot;
							</span>
							<span>
								<LuEye
									size={10}
									style={{
										display: 'inline',
										verticalAlign: '-1px',
										marginRight: 3,
									}}
								/>
								{announcement.viewCount}
							</span>
						</>
					)}
				</div>

				{/* Preview */}
				<div className="flex-1" style={{ minHeight: 0, overflow: 'hidden' }}>
					<p
						className="leading-relaxed"
						style={{
							fontSize: 13,
							color: '#475569',
							display: '-webkit-box',
							WebkitLineClamp: thumbnailUrl ? 3 : 11,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							margin: 0,
						}}
					>
						{plainContent}
					</p>
				</div>

				{/* Read more */}
				<button
					type="button"
					className="mt-4 flex items-center gap-1 self-start"
					style={{
						fontSize: 14,
						fontWeight: 600,
						color: accentColor,
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						padding: 0,
						transition: 'color 0.2s',
					}}
					onClick={(e) => {
						e.stopPropagation();
						onReadMore(announcement, thumbnailUrl);
					}}
					onMouseOver={(e) => {
						e.currentTarget.style.color = darkenHex(accentColor);
					}}
					onMouseOut={(e) => {
						e.currentTarget.style.color = accentColor;
					}}
				>
					{ml('Read more', currentUser, allMultiLingualData)}
					<span
						style={{ transition: 'transform 0.2s', display: 'inline-block' }}
					>
						&rarr;
					</span>
				</button>
			</div>
		</div>
	);
}

export default AnnouncementCard;
